import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiLogIn, FiMail, FiLock, FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { login as loginService } from '../../services/auth/authService';

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  overflow: hidden;
`;

const TopGlow = styled.div`
  position: absolute;
  top: -150px;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 0, 254, 0.4) 0%, rgba(0, 0, 0, 0) 70%);
  z-index: 1;
`;

const GridBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(rgba(20, 20, 20, 0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(20, 20, 20, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 450px;
  background-color: rgba(15, 15, 25, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 8px 30px rgba(0, 0, 254, 0.15);
  border: 1px solid rgba(0, 0, 254, 0.1);
  position: relative;
  z-index: 10;
  
  @media (max-width: 500px) {
    padding: 30px 20px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const Logo = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0000fe, #6060ff);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 20px;
  box-shadow: 0 0 20px rgba(0, 0, 254, 0.3);
`;

const Heading = styled.h1`
  color: white;
  font-size: 24px;
  text-align: center;
  margin-bottom: 30px;
  font-weight: 700;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 24px;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
  font-size: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 16px 16px 50px;
  background-color: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: white;
  font-size: 16px;
  outline: none;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: rgba(0, 0, 254, 0.5);
    box-shadow: 0 0 0 2px rgba(0, 0, 254, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ForgotPassword = styled(Link)`
  display: block;
  text-align: right;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-top: -16px;
  margin-bottom: 24px;
  text-decoration: none;
  
  &:hover {
    color: #0000fe;
  }
`;

const Button = styled.button`
  width: 100%;
  background: linear-gradient(90deg, #0000fe, #6060ff);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 16px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  margin-bottom: 20px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(0, 0, 254, 0.25);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: linear-gradient(90deg, #333, #444);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const OrDivider = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  color: rgba(255, 255, 255, 0.5);
  
  &:before, &:after {
    content: '';
    flex: 1;
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:before {
    margin-right: 10px;
  }
  
  &:after {
    margin-left: 10px;
  }
`;

const LinkText = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  
  a {
    color: #6060ff;
    text-decoration: none;
    font-weight: 600;
    margin-left: 5px;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  margin-top: 5px;
  margin-bottom: 15px;
  text-align: center;
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const data = await loginService(email, password);
      login(data.user, data.token);
      setIsLoading(false);
      navigate('/chat');
    } catch (err) {
      setIsLoading(false);
      setError(err.message || 'An error occurred during login');
    }
  };

  return (
    <LoginContainer>
      <TopGlow />
      <GridBackground />
      
      <LoginCard>
        <LogoContainer>
          <Logo>AI</Logo>
        </LogoContainer>
        
        <Heading>Log in to your account</Heading>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <form onSubmit={handleSubmit}>
          <InputGroup>
            <InputIcon>
              <FiMail />
            </InputIcon>
            <Input
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>
          
          <InputGroup>
            <InputIcon>
              <FiLock />
            </InputIcon>
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </InputGroup>
          
          <ForgotPassword to="/request-invite">Forgot password?</ForgotPassword>
          
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Logging in...' : (
              <>
                <FiLogIn />
                Log in
              </>
            )}
          </Button>
        </form>
        
        <OrDivider>or</OrDivider>
        
        <LinkText>
          Don't have an account?
          <Link to="/signup">Sign up</Link>
        </LinkText>
        
        <LinkText style={{ marginTop: '10px' }}>
          Need an invite code?
          <Link to="/request-invite">Request invite</Link>
        </LinkText>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
